import { Box, Button, styled, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import { Retailer } from "@/redux/types";
import { ResponsiveDialog } from "@brandclub/common-ui";
import { create } from "zustand";

interface DialogState {
  onSaveCredentials: (value: any) => void;
  cardLastFour?: string;
  retailer?: Retailer;
}

const defaultState: DialogState = {
  onSaveCredentials: async (_value: any) => {},
  cardLastFour: "1234",
  retailer: undefined,
};

const useDialogStore = create<
  DialogState & { open: boolean; close: () => void }
>((set) => ({
  ...defaultState,
  open: false,
  close: () => set({ open: false }),
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  flexDirection: "column",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  color: theme.vars.palette.text.primary,
  ".retailer-connect-login-motion-container": {
    padding: "34px 24px 55px 24px",
    [theme.breakpoints.down("md")]: {
      padding: "34px 20px 22px 20px",
    },
  },
}));

const RetailerLogo = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "32px",
  "& img": {
    height: "40px",
  },
});

const PaymentVerificationPopup = () => {
  const { open, close, onSaveCredentials, cardLastFour, retailer } =
    useDialogStore();
  const [cvvCode, setCvvCode] = useState("");

  const handleContinue = () => {
    if (cvvCode.length >= 3) {
      onSaveCredentials({ cvvCode });
      close();
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={close}>
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            width: 580,
          },
          overflowY: "auto",
          maxWidth: "100%",
          backgroundColor: "white",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            <RetailerLogo>
              <img src={retailer?.logo} alt="Retailer Logo" />
            </RetailerLogo>

            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                marginBottom: "16px",
                color: "#232F3E",
                fontSize: "24px",
              }}
            >
              Verify your payment method
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#565959",
                marginBottom: "32px",
                fontSize: "14px",
                lineHeight: "20px",
              }}
            >
              For added security, enter the CVV code for
              <br />
              your Amazon default card ending in {cardLastFour}.
            </Typography>

            <Box
              sx={{ width: "100%", maxWidth: "420px", marginBottom: "32px" }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  marginBottom: "8px",
                  color: "#565959",
                  fontSize: "12px",
                  textAlign: "left",
                }}
              >
                CVV code
              </Typography>
              <CheckoutInput
                label={"CVV code"}
                style={{ marginBottom: 16 }}
                onChange={(e) => {
                  setCvvCode(e.target.value as any);
                }}
                sx={(theme) => ({
                  ".MuiFilledInput-root": {
                    borderColor: theme.vars.palette.divider,
                  },
                })}
                value={cvvCode}
                InputProps={{
                  autoComplete: "username",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
              />
            </Box>

            <Button
              onClick={handleContinue}
              disabled={cvvCode.length < 3}
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#232F3E",
                color: "white",
                padding: "12px",
                fontSize: "14px",
                fontWeight: "normal",
                textTransform: "none",
                borderRadius: "8px",
                maxWidth: "300px",
                "&:hover": {
                  backgroundColor: "#37475A",
                },
                "&:disabled": {
                  backgroundColor: "#F7F8F8",
                  color: "#565959",
                },
              }}
            >
              Continue
            </Button>
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

PaymentVerificationPopup.show = (props: DialogState) => {
  useDialogStore.setState({
    ...props,
    open: true,
  });
};

PaymentVerificationPopup.close = () => {
  useDialogStore.setState({
    ...defaultState,
    open: false,
  });
};

export default PaymentVerificationPopup;
